import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartToolTip,
  TooltipProps as RechartsTooltipProps,
  BarProps,
} from 'recharts';
import { useAtomValue } from 'jotai';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { Box, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { ReactNode, memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  SummaryBox,
  StyledLegend,
  StyledSelect,
  Title,
  StyledLegendForCombinedRisk,
} from './style';
import { ColorSquare, ColorLine } from '@/components/Analysis/DeckPlan/ColorLegend/ColorLegend';
import { unitSystem } from '@/components/Analysis/state';
import { localiseMeasurement } from '@/utils/unitSystem';
import { UnitsEnum, UnitSystemEnum } from '@/__generated__/graphql';
import { titleCase } from '@/utils';
import { abyssColors } from '@/theme/colors';

interface ChartDataItem {
  [key: string]: number | string;
}

type RecordType = { [key: string]: string | number };

type Props = {
  title: string;
  data: RecordType[];
  legendData: { [key: string]: string };
  trimLegend?: boolean;
  showRecordsLimit?: boolean;
  containerWidth: number;
  customLabel?: ReactNode;
  removeZeroValueRecords?: boolean;
  hideLegend?: boolean;
  showBarOutline?: boolean;
  barSize?: number;
  sortBy?: 'high-to-low' | 'low-to-high';
  isCoatingFilter?: boolean;
};

const StyledLegendContainer = ({
  label,
  color,
  lineChartLabels,
}: {
  label: string;
  color: string;
  lineChartLabels?: string[];
}) => (
  <Box style={{ display: 'flex', alignItems: 'center' }}>
    {lineChartLabels?.includes(label) ? (
      <StyledLegendForCombinedRisk bgcolor={color} />
    ) : (
      <StyledLegend bgcolor={color} />
    )}
    <Box style={{ fontSize: '1.1rem', fontWeight: 500 }}>{label.toUpperCase()}</Box>
  </Box>
);

export const renderLegend = (
  legendData: { [key: string]: string },
  trimLegend: boolean,
  riskRemediationChart?: boolean,
  lineChartLabels?: string[]
) => {
  const legend = trimLegend ? Object.entries(legendData).slice(0, 3) : Object.entries(legendData);
  const remainingLegend = trimLegend
    ? Object.entries(legendData)
        .slice(3)
        .map(([label, color]) => ({
          label,
          color,
        }))
    : [];

  return (
    <Tooltip
      title={
        trimLegend ? (
          <Stack direction="row" spacing={1.5} justifyContent="flex-start">
            {remainingLegend.map(({ label, color }) => (
              <StyledLegendContainer
                key={label}
                label={label}
                color={color}
                lineChartLabels={lineChartLabels}
              />
            ))}
          </Stack>
        ) : (
          ''
        )
      }
    >
      <Stack
        sx={{
          ...(trimLegend && { cursor: 'pointer' }),
          ml: 7.2,
          mt: riskRemediationChart ? 1 : 0,
        }}
        direction="row"
        spacing={1.5}
        justifyContent={riskRemediationChart ? 'center' : 'flex-start'}
      >
        {legend.map(([label, color]) => (
          <StyledLegendContainer
            key={label}
            label={riskRemediationChart ? label : titleCase(label)}
            color={color}
            lineChartLabels={lineChartLabels}
          />
        ))}
        {trimLegend && remainingLegend.length > 0 && (
          <Box style={{ fontSize: '1.1rem', fontWeight: 500 }}>...</Box>
        )}
      </Stack>
    </Tooltip>
  );
};

interface DataObject {
  name: string;
  [key: string]: number | string;
}

interface CustomTooltipProps extends RechartsTooltipProps<ValueType, NameType> {
  customLabel?: ReactNode;
  selectedUnitSystem?: string;
  legendData?: { [key: string]: string };
  showUnit?: boolean;
  riskRemediationDataChart?: boolean;
  lineChartLabels?: string[];
  isCoatingFilter?: boolean;
}

const GetBarRenderer = (outline: boolean) => {
  return (props: BarProps) => {
    const { fill, x, y, width, height } = props;
    const offsetY = y ? Number(y) - 2 : y;
    if (Number(height) < 1) {
      return <rect />;
    }
    return (
      <rect
        x={x}
        y={offsetY}
        width={width}
        height={(height ?? 0) + 2}
        fill={fill}
        stroke={outline ? 'black' : 'none'}
        strokeOpacity={0.2}
        rx={2}
      />
    );
  };
};

const formatCoatingRawValue = (
  payload: Record<string, unknown>,
  dataKey: NameType,
  selectedUnitSystem?: string
): string => {
  const rawValueKey = `__raw_${String(dataKey)}`;
  const rawValue = payload[rawValueKey];

  if (typeof rawValue !== 'number') {
    return '0';
  }

  const convertedValue = selectedUnitSystem
    ? localiseMeasurement({
        unitSystem: selectedUnitSystem as UnitSystemEnum,
        sourceUnit: UnitsEnum.SquareMeter,
        value: rawValue,
        showUnitLabel: false,
      }) ?? rawValue
    : rawValue;

  return Math.round(Number(convertedValue)).toLocaleString();
};

export const CustomTooltip = ({
  active,
  payload,
  customLabel,
  legendData,
  selectedUnitSystem,
  showUnit = true,
  riskRemediationDataChart = false,
  lineChartLabels,
  isCoatingFilter = false,
}: CustomTooltipProps) => {
  if (active) {
    let sortedPayload = payload?.reverse();
    if (riskRemediationDataChart && legendData) {
      const legendKeys = Object.keys(legendData);
      sortedPayload = payload?.sort((a, b) => {
        const indexA = legendKeys.indexOf(a?.name?.toString() ?? '');
        const indexB = legendKeys.indexOf(b?.name?.toString() ?? '');
        return indexA - indexB;
      });
    }
    const getMatchingLegendKey = (itemName: string | undefined) => {
      if (!itemName) return '';
      if (!legendData) return titleCase(itemName);
      let matchedKey = Object.keys(legendData).find(
        (key) => key.toLowerCase() === itemName.toLowerCase()
      );

      if (!matchedKey)
        matchedKey = Object.keys(legendData).find((key) =>
          key.toLowerCase().includes(itemName.toLowerCase())
        );
      return titleCase(matchedKey ?? itemName);
    };
    return (
      <Stack sx={{ backgroundColor: '#fff', p: 2, borderRadius: 1 }}>
        {sortedPayload?.map((item) => {
          const dataKey =
            riskRemediationDataChart && item?.dataKey ? item?.dataKey : item?.name ?? '';
          const percentageValue = item.payload[dataKey];

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minWidth: '150px',
              }}
              key={item.name}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                {item?.name && lineChartLabels?.includes(item?.name.toString()) ? (
                  <ColorLine color={item.color ?? abyssColors.neutral[500]} />
                ) : (
                  <ColorSquare color={item.color ?? abyssColors.neutral[500]} />
                )}

                <Typography fontSize="1rem" sx={{ ml: 1 }}>
                  {riskRemediationDataChart
                    ? item.name
                    : `${getMatchingLegendKey(item.name?.toString())}:`}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isCoatingFilter ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography fontSize="1rem" fontWeight={600}>
                      {formatCoatingRawValue(item.payload, dataKey, selectedUnitSystem)}
                    </Typography>
                    {selectedUnitSystem && (
                      <Typography
                        fontSize="1rem"
                        fontWeight={600}
                        color={abyssColors.primary[300]}
                        sx={{ ml: 0.5 }}
                      >
                        {selectedUnitSystem === UnitSystemEnum.Imperial ? 'ft²' : 'm²'}
                      </Typography>
                    )}
                    <Typography fontSize="1rem" fontWeight={600} sx={{ ml: 0.5 }}>
                      (
                      {typeof percentageValue === 'number'
                        ? `${percentageValue.toFixed(1)}%`
                        : '0%'}
                      )
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography fontSize="1rem">
                      {(() => {
                        const value = item.payload[dataKey];
                        return typeof value === 'number'
                          ? Math.round(value).toLocaleString()
                          : value;
                      })()}
                    </Typography>
                    {customLabel && showUnit && (
                      <Typography fontSize="1rem" color={abyssColors.primary[300]} sx={{ ml: 0.5 }}>
                        {selectedUnitSystem === UnitSystemEnum.Imperial ? '(ft²)' : '(m²)'}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    );
  }

  return <></>;
};

const defaultRecordsLimit = 15;

const GenericBarChartBase = ({
  title,
  data,
  legendData,
  showRecordsLimit = false,
  trimLegend = false,
  containerWidth,
  customLabel,
  removeZeroValueRecords = false,
  hideLegend = false,
  showBarOutline = false,
  barSize,
  sortBy,
  isCoatingFilter = false,
}: Props) => {
  const [limitedRecords, setLimitedRecords] = useState(data);
  const [selectedValue, setSelectedValue] = useState<number>(defaultRecordsLimit);
  const selectedUnitSystem = useAtomValue(unitSystem);
  const graphWidth = useMemo(() => {
    const largestGraphLabelLength = data.reduce(
      (max, item) => Math.max(max, item.name.toString().length * 7),
      0
    );

    const graphContainerWidth = (showRecordsLimit ? 0.7 : 0.3) * containerWidth - 12;
    return Math.max(selectedValue * (largestGraphLabelLength + 16), graphContainerWidth);
  }, [data, containerWidth, selectedValue, showRecordsLimit]);

  const removeZeroValuesFromArray = useCallback((chartData: RecordType[]): RecordType[] => {
    const result: RecordType[] = [];
    for (const record of chartData) {
      const resultObject: RecordType = {};
      for (const key of Object.keys(record)) {
        if (Object.hasOwn(record, key) && record[key] !== 0) {
          resultObject[key] = record[key];
        }
      }
      result.push(resultObject);
    }
    return result;
  }, []);

  const dataLength = useMemo(
    () => (removeZeroValueRecords ? removeZeroValuesFromArray(data).length : data.length),
    [data, removeZeroValueRecords, removeZeroValuesFromArray]
  );

  useEffect(() => {
    if (dataLength && dataLength < defaultRecordsLimit) setSelectedValue(dataLength);
  }, [data, dataLength, removeZeroValueRecords, removeZeroValuesFromArray]);

  const calculateSum = (item: ChartDataItem) => {
    return Object.values(item)
      .filter((value): value is number => typeof value === 'number')
      .reduce((accumulator, current) => accumulator + current, 0);
  };

  const sortChartsData = useCallback(
    (chartData: ChartDataItem[]) => {
      return chartData.sort((a, b) => {
        if (isCoatingFilter) {
          // For coating condition, sort by RI5 + RI4 percentage
          const getRi5Ri4Sum = (item: ChartDataItem) => {
            // Try different case variations for RI keys
            const ri5 =
              (item['ri5'] as number) || (item['Ri5'] as number) || (item['RI5'] as number) || 0;
            const ri4 =
              (item['ri4'] as number) || (item['Ri4'] as number) || (item['RI4'] as number) || 0;
            return ri5 + ri4;
          };
          const sumA = getRi5Ri4Sum(a);
          const sumB = getRi5Ri4Sum(b);
          if (sortBy === 'low-to-high') {
            return sumA - sumB;
          } else {
            return sumB - sumA;
          }
        }
        if (sortBy === 'low-to-high') {
          return calculateSum(a) - calculateSum(b); // smallest first
        } else {
          return calculateSum(b) - calculateSum(a); // largest first (default)
        }
      });
    },
    [sortBy, isCoatingFilter]
  );

  const convertData = useCallback(
    (records: ChartDataItem[]): DataObject[] => {
      return records.map((record) =>
        Object.keys(record).reduce((accumulator: DataObject, key: string) => {
          if (key !== 'name' && typeof record[key] === 'number') {
            accumulator[key] =
              localiseMeasurement({
                unitSystem: selectedUnitSystem,
                sourceUnit: UnitsEnum.SquareMeter,
                value: Number(record[key]),
                showUnitLabel: false,
              }) ?? 0;
          } else {
            accumulator[key] = record[key];
          }
          return accumulator;
        }, {} as DataObject)
      );
    },
    [selectedUnitSystem]
  );

  useEffect(() => {
    const filteredData = removeZeroValueRecords ? removeZeroValuesFromArray(data) : data;
    const sortedData = sortChartsData(filteredData).slice(0, selectedValue);
    const finalData = customLabel ? convertData(sortedData) : sortedData;

    setLimitedRecords(finalData);
  }, [
    data,
    selectedValue,
    selectedUnitSystem,
    customLabel,
    removeZeroValueRecords,
    removeZeroValuesFromArray,
    sortChartsData,
    convertData,
  ]);

  const barChartData = useMemo(() => {
    if (data.length === 0) return legendData;

    const transformedLegend: { [key: string]: string } = {};

    const allDataKeys = [
      ...new Set(
        data.flatMap((entry) =>
          Object.keys(entry).filter((key) => key !== 'name' && !key.startsWith('__raw_'))
        )
      ),
    ].reverse();

    const legendLookup = Object.keys(legendData).reduce((accumulator, key) => {
      accumulator[key.toLowerCase()] = legendData[key];
      return accumulator;
    }, {} as { [key: string]: string });

    allDataKeys.forEach((dataKey) => {
      const lowerDataKey = dataKey.toLowerCase();

      if (legendLookup[lowerDataKey]) {
        transformedLegend[dataKey] = legendLookup[lowerDataKey];
      } else {
        for (const legendKey in legendData) {
          if (
            legendKey.toLowerCase().includes(lowerDataKey) ||
            lowerDataKey.includes(legendKey.toLowerCase())
          ) {
            transformedLegend[dataKey] = legendData[legendKey];
            break;
          }
        }
      }
    });

    return transformedLegend;
  }, [data, legendData]);

  return (
    <SummaryBox width={showRecordsLimit ? `${graphWidth}px` : 'auto'}>
      <Stack
        sx={{ margin: '0px 22px 0px 22px' }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box component="h4" sx={{ m: 0, height: '30px' }}>
          <Title>{title}</Title>
        </Box>
        {showRecordsLimit && selectedValue >= defaultRecordsLimit && (
          <StyledSelect
            size="small"
            variant="outlined"
            value={selectedValue}
            onChange={(event) => setSelectedValue(Number(event.target.value))}
          >
            <MenuItem value={15}>Top 15</MenuItem>
            <MenuItem value={25}>Top 25</MenuItem>
            <MenuItem value={dataLength}>All</MenuItem>
          </StyledSelect>
        )}
      </Stack>
      <ResponsiveContainer id="bar-chart-container">
        <BarChart
          margin={{ left: 0, right: 20, top: 30, bottom: -10 }}
          data={limitedRecords}
          barSize={barSize ?? 10}
          reverseStackOrder
        >
          <CartesianGrid vertical={false} color={abyssColors.primary[200]} opacity={0.2} />
          <XAxis
            domain={['auto', 'auto']}
            allowDataOverflow
            interval={0}
            tick={{ fontSize: '1rem', color: abyssColors.primary[400] }}
            tickLine={false}
            dataKey="name"
            axisLine={false}
            opacity={0.8}
          />
          <YAxis
            domain={isCoatingFilter ? [0, 100] : ['auto', 'auto']}
            tick={{ fontSize: '1rem', color: abyssColors.primary[400] }}
            opacity={0.8}
            tickLine={false}
            axisLine={false}
            tickCount={6}
            tickFormatter={(value) => {
              if (isCoatingFilter) {
                return `${Math.round(value)}%`;
              }
              return new Intl.NumberFormat('en-US', {
                notation: 'compact',
                compactDisplay: 'short',
              }).format(value);
            }}
          >
            {customLabel}
          </YAxis>
          <ChartToolTip
            content={
              <CustomTooltip
                customLabel={customLabel}
                selectedUnitSystem={selectedUnitSystem}
                legendData={legendData}
                isCoatingFilter={isCoatingFilter}
              />
            }
          />
          {hideLegend || (
            <Legend align="right" content={() => renderLegend(legendData, trimLegend)} />
          )}

          {Object.entries(barChartData).map(([key, color]) => (
            <Bar
              key={key}
              dataKey={key}
              fill={color}
              stackId={title}
              shape={GetBarRenderer(showBarOutline)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </SummaryBox>
  );
};

export const GenericBarChart = memo(GenericBarChartBase);
